import { importReviewReasons, reviewDecisionComment } from "../lib/importReviewReasons";
import { useInHub } from "../lib/hubContext";
import QualityReviewDocuments from "../components/QualityReviewDocuments";
import ArticleAssignmentReviewPage from "./ArticleAssignmentReviewPage";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  getImportQualityReview,
  getImportQualityReviewHistory,
  recordImportQualityReviewDecision,
  type ImportQualityReviewAction,
  type ImportQualityReviewFinding,
  type ImportQualityReviewHistoryResponse,
  type ImportQualityReviewKind,
  type ImportQualityReviewResponse,
  type ImportQualityReviewStatus,
} from "../api/imports";
import {
  fetchSupplierSourceProfiles,
  type SupplierSourceProfileCheck,
} from "../api/sourceProfiles";
import { useSession } from "../auth/useSession";
import {
  FilterToolbar,
  TableEmptyRow,
  TableErrorRow,
  TableHeadCell,
  TableLoadMoreFooter,
  TableLoadingRow,
  TablePanel,
} from "../components/DataTable";
import LineAmountCorrectionHistory from "../components/LineAmountCorrectionHistory";
import LineAmountCorrection from "../components/LineAmountCorrection";
import { isDemoMode } from "../lib/env";
import QualityReviewOriginal from "../components/QualityReviewOriginal";
import DocumentPdfActions from "../components/DocumentPdfActions";
import FilterDropdown from "../components/FilterDropdown";
import ImportSourcesNav from "../components/ImportSourcesNav";
import { ApiError } from "../lib/api";
import {
  formatDateShort,
  formatDateTimeShort,
  formatEuro,
  formatPercent,
  formatQty,
} from "../lib/format";
import { canManageDataQuality } from "../lib/role";
import { useEscapeClosableDetails } from "../lib/useEscapeClosableDetails";
import { useEscapeKey } from "../lib/useEscapeKey";

const PAGE_SIZE = 50;
type ReviewListStatus = ImportQualityReviewStatus | "history";

const REVIEW_KINDS: Array<{
  value: ImportQualityReviewKind;
  label: string;
  shortLabel: string;
  description: string;
}> = [
  {
    value: "parse_validation_line",
    label: "Preiszeilen",
    shortLabel: "Preiszeile",
    description:
      "Preis-, Mengen- oder Einheitshinweise aus importierten Belegpositionen fachlich einordnen.",
  },
  {
    value: "reconciliation_supplier_case",
    label: "Belegabgleich",
    shortLabel: "Belegabgleich",
    description:
      "Fehlende Gegenstücke, nicht zugeordnete Positionen und Wertabweichungen zwischen UGL und PDF einordnen.",
  },
  {
    value: "set_price_qa_set",
    label: "Artikelsets",
    shortLabel: "Artikelset",
    description:
      "Setpreis, Summe der Einzelpreise und fehlende Preisangaben nachvollziehen.",
  },
];

const STATUS_OPTIONS: Array<{
  value: ReviewListStatus;
  label: string;
}> = [
  { value: "open", label: "Offen" },
  { value: "action_required", label: "Korrektur nötig" },
  { value: "resolved", label: "Plausibel" },
  { value: "all", label: "Alle Status" },
  { value: "history", label: "Verlauf" },
];

const ACTION_OPTIONS: Array<{
  value: ImportQualityReviewAction;
  label: string;
  description: string;
}> = [
  {
    value: "resolve_plausible",
    label: "Plausibel",
    description: "Der Hinweis ist nachvollziehbar; die Quelle bleibt unverändert.",
  },
  {
    value: "mark_correction_required",
    label: "Korrektur erforderlich",
    description: "Der Prüfpunkt muss fachlich oder in der Quelldatei geklärt werden.",
  },
];

const SECONDARY_BUTTON_CLASS =
  "inline-flex h-9 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-default disabled:opacity-50";
const PRIMARY_BUTTON_CLASS =
  "inline-flex h-9 items-center justify-center rounded-md bg-emerald-600 px-3 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-default disabled:opacity-50";
const DIALOG_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function ImportQualityReviewPage() {
  const [params] = useSearchParams();
  return params.get("check") === "article_assignment" ? <ArticleAssignmentReviewPage /> : <QualityReviewPage />;
}

function QualityReviewPage() {
  const inHub = useInHub();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const supplier = supplierFromParam(searchParams.get("supplier"));
  const kind = kindFromParam(searchParams.get("check"));
  const status = statusFromParam(searchParams.get("status"));
  const grouped = kind === "parse_validation_line" && status !== "history";
  const documentId = searchParams.get("doc");
  const [worklistMessage, setWorklistMessage] = useState("");
  const selectedFindingKey = normalizeFindingKey(searchParams.get("item"));
  const selectedHistoryEventId = normalizeFindingKey(searchParams.get("event"));
  const profilesQuery = useQuery({
    queryKey: ["source-coverage", "profiles"],
    queryFn: fetchSupplierSourceProfiles,
    staleTime: 30_000,
    retry: false,
  });
  const sourceProfiles = profilesQuery.data?.profiles ?? [];
  const sourceProfile =
    sourceProfiles.find((profile) => profile.supplier === supplier) ?? null;
  const selectedCheck =
    sourceProfile?.checks.find((check) => check.kind === kind) ?? null;
  const canLoadCurrentReview =
    status === "history" || selectedCheck?.status === "active";
  const activeReviewKinds = REVIEW_KINDS.filter(
    (item) =>
      sourceProfile?.checks.some(
        (check) => check.kind === item.value && check.status === "active",
      ) ?? false,
  );
  const inactiveChecks =
    sourceProfile?.checks.filter((check) => check.status !== "active") ?? [];
  const supplierOptions =
    sourceProfiles.length > 0
      ? sourceProfiles.map((profile) => ({
          value: profile.supplier,
          label: profile.supplier,
        }))
      : [{ value: supplier, label: supplier }];

  const findingsQuery = useInfiniteQuery({
    queryKey: ["imports", "quality-review", supplier, kind, status],
    queryFn: async ({ pageParam, signal }) => {
      if (status === "history") {
        const response = await getImportQualityReviewHistory({
          supplier,
          kind,
          limit: PAGE_SIZE,
          offset: pageParam,
          signal,
        });
        return historyResponseToReviewPage(response);
      }
      return getImportQualityReview({
        supplier,
        kind,
        status,
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: ImportQualityReviewResponse) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit || nextOffset >= lastPage.total) {
        return undefined;
      }
      return nextOffset;
    },
    enabled: canLoadCurrentReview && !grouped,
    retry: false,
  });
  const hasListData = Boolean(findingsQuery.data);
  const listBlockingError = findingsQuery.isError && !hasListData;

  const detailQuery = useQuery({
    queryKey: [
      "imports",
      "quality-review",
      "finding",
      supplier,
      kind,
      status,
      selectedFindingKey,
      selectedHistoryEventId,
    ],
    queryFn: async ({ signal }) => {
      if (status === "history") {
        const response = await getImportQualityReviewHistory({
          supplier,
          kind,
          limit: selectedHistoryEventId ? 1 : 100,
          offset: 0,
          findingKey: selectedFindingKey,
          eventId: selectedHistoryEventId,
          signal,
        });
        const page = historyResponseToReviewPage(response);
        const selected = selectedHistoryEventId
          ? page.items.find(
              (item) => item.history_event_id === selectedHistoryEventId,
            )
          : page.items[0];
        return { ...page, items: selected ? [selected] : [] };
      }
      return getImportQualityReview({
        supplier,
        kind,
        status: "all",
        limit: 1,
        offset: 0,
        findingKey: selectedFindingKey,
        signal,
      });
    },
    enabled:
      Boolean(selectedFindingKey) &&
      canLoadCurrentReview &&
      (grouped || (hasListData && !findingsQuery.isFetching)),
    retry: false,
  });

  const isRefreshing =
    findingsQuery.isFetching &&
    !findingsQuery.isFetchingNextPage &&
    !findingsQuery.isLoading;
  const findings = useMemo(
    () =>
      isRefreshing
        ? []
        : (findingsQuery.data?.pages.flatMap((page) => page.items) ?? []),
    [findingsQuery.data, isRefreshing],
  );
  const firstPage = findingsQuery.data?.pages[0];
  const total = firstPage?.total ?? null;
  const selectedFinding = detailQuery.data?.items[0] ?? null;
  const activeKind = reviewKindConfig(kind);
  const showRowStatus = status !== "open";
  const tableColumnCount = showRowStatus ? 4 : 3;
  const detailWaitingForList =
    !grouped && Boolean(selectedFindingKey) &&
    !listBlockingError &&
    (!hasListData || findingsQuery.isFetching);
  const detailError = listBlockingError
    ? findingsQuery.error
    : detailQuery.isError
      ? detailQuery.error
      : null;

  const setFilter = useCallback(
    (
      key: "supplier" | "check" | "status",
      value: string,
    ) => {
      const next = new URLSearchParams(searchParams);
      next.set(key, value);
      next.delete("item");
      next.delete("event");
      next.delete("doc");
      setWorklistMessage("");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const selectFinding = useCallback(
    (findingKey: string | null, historyEventId?: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (findingKey) {
        next.set("item", findingKey);
        if (historyEventId) next.set("event", historyEventId);
        else next.delete("event");
        setSearchParams(next, {
          state: { importQualityDrawer: true },
        });
      } else {
        next.delete("item");
        next.delete("event");
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const closeFinding = useCallback(() => {
    if (grouped) {
      const next = new URLSearchParams(searchParams);
      next.delete("item"); next.delete("event");
      const sourceDoc = stringValue(selectedFinding?.evidence_snapshot.invoice_doc_id);
      if (!next.get("doc") && sourceDoc) next.set("doc", sourceDoc);
      setSearchParams(next, { replace: true });
      return;
    }
    const drawerWasOpenedFromList = Boolean(
      location.state &&
        typeof location.state === "object" &&
        "importQualityDrawer" in location.state &&
        location.state.importQualityDrawer,
    );
    if (drawerWasOpenedFromList) {
      navigate(-1);
      return;
    }
    selectFinding(null);
  }, [location.state, navigate, selectFinding, grouped, searchParams, selectedFinding, setSearchParams]);

  return (
    <main className="mx-auto w-full min-w-0 max-w-7xl px-6 py-8">
      {inHub ? null : <nav className="text-xs text-slate-500">
        <Link to="/imports/verlauf" className="hover:text-emerald-700 hover:underline">
          Importe
        </Link>{" "}
        / <span className="text-slate-700">Qualitätsprüfung</span>
      </nav>}

      <header className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          {inHub ? null : <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
            Import-Qualität prüfen
          </h1>}
        </div>
        <Link
          to="/coverage?tab=files"
          className={SECONDARY_BUTTON_CLASS}
        >
          Quellen ansehen
        </Link>
      </header>

      <div className="mt-5">
        <ImportSourcesNav active="imports" />
      </div>

      <FilterToolbar className="mt-4">
        <FilterDropdown
          label="Großhändler"
          options={supplierOptions}
          value={supplier}
          defaultValue={supplier}
          showValue
          onChange={(value) => setFilter("supplier", value)}
          ariaLabel="Großhändler filtern"
        />
        <FilterDropdown
          label="Status"
          options={STATUS_OPTIONS}
          value={status}
          defaultValue="open"
          showValue
          onChange={(value) => setFilter("status", value)}
          ariaLabel="Prüfstatus filtern"
        />
      </FilterToolbar>

      {activeReviewKinds.length > 0 ? (
        <ReviewKindTabs
          items={activeReviewKinds}
          value={kind}
          onChange={(value) => setFilter("check", value)}
        />
      ) : null}

      {inactiveChecks.length > 0 ? (
        <InactiveReviewKinds supplier={supplier} checks={inactiveChecks} />
      ) : null}

      {status !== "history" && profilesQuery.isLoading ? (
        <ReviewAvailabilityState
          title="Prüfumfang wird geladen"
          description="TruuCost prüft, welche Quellen für diesen Großhändler erwartet werden."
        />
      ) : status !== "history" && (profilesQuery.isError || !sourceProfile) ? (
        <ReviewAvailabilityState
          title="Prüfumfang nicht verfügbar"
          description="Welche Qualitätsprüfungen für diesen Großhändler anwendbar sind, konnte nicht geladen werden."
        />
      ) : selectedCheck?.status !== "active" && status !== "history" ? (
        <InactiveReviewKindState
          supplier={supplier}
          kind={kind}
          check={selectedCheck}
        />
      ) : (
        <>
      {!grouped && <section className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              {activeKind.label}
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">
              {activeKind.description}
            </p>
          </div>
          <span className="text-sm tabular-nums text-slate-500">
            {total === null
              ? "–"
              : `${total.toLocaleString("de-AT")} ${
                  total === 1 ? "Prüfpunkt" : "Prüfpunkte"
                }`}
          </span>
        </div>
      </section>}

      {grouped ? <QualityReviewDocuments supplier={supplier} status={status} documentId={documentId} message={worklistMessage} paused={Boolean(selectedFindingKey)}
        onStatus={nextStatus => setFilter("status", nextStatus)}
        onDocument={id => { const next = new URLSearchParams(searchParams); next.delete("item"); next.delete("event"); if (id) next.set("doc", id); else next.delete("doc"); setWorklistMessage(""); setSearchParams(next); }}
        onOpen={key => { setWorklistMessage(""); selectFinding(key); }} /> : <TablePanel
        className="min-w-0"
        title="Arbeitsliste"
        subtitle={`${supplier} · ${statusLabel(status)}`}
        footer={
          findingsQuery.isLoading || listBlockingError || isRefreshing ? null : findingsQuery.isFetchNextPageError ? (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm text-slate-600">
              <span>Weitere Prüfpunkte konnten nicht geladen werden.</span>
              <button
                type="button"
                className={SECONDARY_BUTTON_CLASS}
                onClick={() => void findingsQuery.fetchNextPage()}
              >
                Weitere erneut laden
              </button>
            </div>
          ) : (
            <TableLoadMoreFooter
              loadedCount={findings.length}
              totalCount={total}
              itemLabel="Prüfpunkte"
              moreItemLabel="Prüfpunkte"
              hasMore={Boolean(findingsQuery.hasNextPage)}
              isLoading={findingsQuery.isFetchingNextPage}
              buttonLabel="Weitere Prüfpunkte laden"
              loadingLabel="Lade weitere Prüfpunkte …"
              onLoadMore={() => void findingsQuery.fetchNextPage()}
            />
          )
        }
      >
        <table className="min-w-[780px] divide-y divide-slate-200 text-sm sm:min-w-full">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <TableHeadCell>Prüfpunkt</TableHeadCell>
              {showRowStatus ? <TableHeadCell>Status</TableHeadCell> : null}
              <TableHeadCell>Nachweise</TableHeadCell>
              <TableHeadCell align="right">Aktion</TableHeadCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {findingsQuery.isLoading || isRefreshing ? (
              <TableLoadingRow
                colSpan={tableColumnCount}
                label="Lade Qualitätsprüfungen …"
              />
            ) : listBlockingError ? (
              <TableErrorRow colSpan={tableColumnCount}>
                <div className="flex flex-col items-center gap-2">
                  <span>{reviewLoadError(findingsQuery.error)}</span>
                  <button
                    type="button"
                    className={SECONDARY_BUTTON_CLASS}
                    onClick={() => void findingsQuery.refetch()}
                  >
                    Erneut laden
                  </button>
                </div>
              </TableErrorRow>
            ) : findings.length === 0 ? (
              <TableEmptyRow colSpan={tableColumnCount}>
                {status === "open"
                  ? <>Keine offenen {activeKind.label.toLowerCase()}. Frühere Entscheidungen bleiben nachvollziehbar. <button type="button" className="font-medium text-emerald-800 underline underline-offset-2" onClick={() => setFilter("status", "all")}>Alle Status anzeigen</button></>
                  : `Keine ${activeKind.label.toLowerCase()} für diese Auswahl.`}
              </TableEmptyRow>
            ) : (
              findings.map((finding) => (
                <ReviewFindingRow
                  key={finding.history_event_id ?? finding.finding_key}
                  finding={finding}
                  returnTo={`${location.pathname}${location.search}`}
                  showStatus={showRowStatus}
                  onOpen={() =>
                    selectFinding(
                      finding.finding_key,
                      finding.history_event_id ?? null,
                    )
                  }
                />
              ))
            )}
          </tbody>
        </table>
      </TablePanel>}

      {canManageDataQuality(user) && !isDemoMode() ? <LineAmountCorrectionHistory /> : null}
      <ImportQualityFindingDrawer
        findingKey={selectedFindingKey}
        finding={selectedFinding}
        isLoading={detailWaitingForList || detailQuery.isLoading}
        error={detailError}
        canDecide={canManageDataQuality(user) && status !== "history"}
        historyMode={status === "history"}
        returnTo={`${location.pathname}${location.search}`}
        onClose={closeFinding}
        onRetry={() => {
          if (listBlockingError) {
            void findingsQuery.refetch();
            return;
          }
          void detailQuery.refetch();
        }}
        onSaved={(savedKey) => {
          void queryClient.invalidateQueries({ queryKey: ["imports", "quality-review"], refetchType: grouped ? "none" : "active" });
          void queryClient.invalidateQueries({ queryKey: ["imports", "quality"] });
          if (savedKey && savedKey !== selectedFindingKey) return;
          setWorklistMessage("Gespeichert. Der Aufgabenstand wird aktualisiert; du kannst anschließend weiterprüfen.");
          closeFinding();
        }}
      />
        </>
      )}
    </main>
  );
}

function InactiveReviewKinds({
  supplier,
  checks,
}: {
  supplier: string;
  checks: SupplierSourceProfileCheck[];
}) {
  const details = useEscapeClosableDetails();
  return (
    <details
      open={details.open}
      className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
    >
      <summary
        ref={details.summaryRef}
        onClick={details.onSummaryClick}
        className="cursor-pointer rounded-sm text-xs font-medium text-slate-600 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1"
      >
        {checks.length === 1
          ? "1 Prüfart nicht aktiv"
          : `${checks.length.toLocaleString("de-AT")} Prüfarten nicht aktiv`}
      </summary>
      <ul className="mt-3 grid gap-2 md:grid-cols-2">
        {checks.map((check) => (
          <li
            key={check.kind}
            className="rounded-md border border-slate-200 bg-white px-3 py-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold text-slate-800">
                {reviewKindConfig(check.kind).label}
              </span>
              <SourceProfileCheckBadge check={check} />
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {check.reason}
            </p>
            <Link
              to={`/imports/review?supplier=${encodeURIComponent(supplier)}&check=${check.kind}&status=history`}
              className="mt-2 inline-flex text-xs font-medium text-slate-600 hover:text-slate-900 hover:underline"
            >
              Verlauf ansehen →
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

function ReviewAvailabilityState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white px-5 py-6 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
        {description}
      </p>
    </section>
  );
}

function InactiveReviewKindState({
  supplier,
  kind,
  check,
}: {
  supplier: string;
  kind: ImportQualityReviewKind;
  check: SupplierSourceProfileCheck | null;
}) {
  const config = reviewKindConfig(kind);
  const waitingForSource = check?.status === "waiting_for_source";
  return (
    <section className="rounded-lg border border-slate-200 bg-white px-5 py-6 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          {waitingForSource
            ? `${config.label} wartet auf eine Quelle`
            : `${config.label} ist nicht anwendbar`}
        </h2>
        {check ? <SourceProfileCheckBadge check={check} /> : null}
      </div>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
        {check?.reason ??
          `Für ${supplier} ist diese Prüfart im aktuellen Quellenprofil nicht aktiv.`}
      </p>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
        Deshalb gibt es für diese Auswahl keine aktuelle Arbeitsliste. Frühere
        Entscheidungen bleiben im <Link className="font-medium text-emerald-800 underline underline-offset-2" to={`/imports/review?${new URLSearchParams({supplier, check: kind, status: "history"})}`}>Verlauf</Link> nachvollziehbar.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/coverage" className={SECONDARY_BUTTON_CLASS}>
          Prüfumfang ansehen
        </Link>
      </div>
    </section>
  );
}

function SourceProfileCheckBadge({
  check,
}: {
  check: SupplierSourceProfileCheck;
}) {
  if (check.status === "waiting_for_source") {
    return (
      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
        Wartet auf Quelle
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
      Nicht anwendbar
    </span>
  );
}

function ReviewKindTabs({
  items,
  value,
  onChange,
}: {
  items: typeof REVIEW_KINDS;
  value: ImportQualityReviewKind;
  onChange: (value: ImportQualityReviewKind) => void;
}) {
  return (
    <nav aria-label="Prüfart" className="mb-4 border-b border-slate-200">
      <ul className="-mb-px flex max-w-full gap-x-1 overflow-x-auto text-sm">
        {items.map((item) => {
          const active = item.value === value;
          return (
            <li key={item.value}>
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => onChange(item.value)}
                className={[
                  "inline-flex whitespace-nowrap border-b-2 px-3 py-2 font-medium transition-colors",
                  active
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700",
                ].join(" ")}
              >
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ReviewFindingRow({
  finding,
  returnTo,
  showStatus,
  onOpen,
}: {
  finding: ImportQualityReviewFinding;
  returnTo: string;
  showStatus: boolean;
  onOpen: () => void;
}) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="max-w-xl px-3 py-3 align-top">
        <div className="font-medium text-slate-900">
          {reviewFindingTitle(finding)}
        </div>
        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
          {finding.summary}
        </p>
        <ReviewFindingFacts finding={finding} />
      </td>
      {showStatus ? (
        <td className="whitespace-nowrap px-3 py-3 align-top">
          <ReviewStatusBadge
            status={finding.effective_status}
            active={finding.active !== false}
          />
        </td>
      ) : null}
      <td className="px-3 py-3 align-top">
        <EvidenceLinks finding={finding} returnTo={returnTo} compact />
      </td>
      <td className="px-3 py-3 text-right align-top">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center whitespace-nowrap text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
        >
          Prüfen →
        </button>
      </td>
    </tr>
  );
}

function reviewFindingTitle(finding: ImportQualityReviewFinding): string {
  const evidence = finding.evidence_snapshot;
  if (finding.finding_kind === "parse_validation_line") {
    const articleCode = stringValue(evidence.article_code);
    const linePos = stringValue(evidence.line_pos);
    if (articleCode && linePos) return `${articleCode} · Position ${linePos}`;
    if (articleCode) return articleCode;
  }
  if (finding.finding_kind === "set_price_qa_set") {
    const setCode = stringValue(evidence.set_code);
    if (setCode) return `Artikelset ${setCode}`;
  }
  return finding.title;
}

function ReviewFindingFacts({
  finding,
}: {
  finding: ImportQualityReviewFinding;
}) {
  const evidence = finding.evidence_snapshot;
  if (finding.finding_kind === "parse_validation_line") {
    const pdf = recordValue(evidence.pdf);
    const datanorm = recordValue(evidence.datanorm);
    return (
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
        <FindingFact label="Beleg" value={stringValue(evidence.doc_number)} />
        <FindingFact
          label="Datum"
          value={formatDateShort(stringValue(evidence.doc_date))}
        />
        <FindingFact
          label="PDF-Preis"
          value={formatEuro(numberValue(pdf.list_unit_price))}
        />
        <FindingFact
          label="Referenz"
          value={formatEuro(numberValue(datanorm.list_price))}
        />
      </div>
    );
  }
  if (finding.finding_kind === "set_price_qa_set") {
    const memberCount = numberValue(evidence.member_count);
    const pricedMemberCount = numberValue(evidence.priced_member_count);
    return (
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
        <FindingFact
          label="Preise"
          value={
            memberCount === null || pricedMemberCount === null
              ? null
              : `${formatQty(pricedMemberCount)} von ${formatQty(memberCount)}`
          }
        />
        <FindingFact
          label="Set"
          value={formatEuro(numberValue(evidence.set_list_price))}
        />
        <FindingFact
          label="Einzeln"
          value={formatEuro(numberValue(evidence.member_sum_list_price))}
        />
        <FindingFact
          label="Abweichung"
          value={formatPercent(numberValue(evidence.relative_delta), "ratio")}
        />
      </div>
    );
  }
  const unmatched =
    (numberValue(evidence.ugl_only_doc_count) ?? 0) +
    (numberValue(evidence.pdf_only_doc_count) ?? 0);
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
      <FindingFact
        label="Abgeglichen"
        value={formatQty(numberValue(evidence.matched_doc_count))}
      />
      <FindingFact label="Ohne Gegenstück" value={formatQty(unmatched)} />
      <FindingFact
        label="Offene Positionen"
        value={formatPercent(
          numberValue(evidence.unmatched_line_rate),
          "ratio",
        )}
      />
    </div>
  );
}

function FindingFact({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) {
  if (!value || value === "–") return null;
  return (
    <span>
      <span className="text-slate-400">{label}</span>{" "}
      <span className="font-medium tabular-nums text-slate-700">{value}</span>
    </span>
  );
}

function ImportQualityFindingDrawer({
  findingKey,
  finding,
  isLoading,
  error,
  canDecide,
  historyMode,
  returnTo,
  onClose,
  onRetry,
  onSaved,
}: {
  findingKey: string | null;
  finding: ImportQualityReviewFinding | null;
  isLoading: boolean;
  error: unknown;
  canDecide: boolean;
  historyMode: boolean;
  returnTo: string;
  onClose: () => void;
  onRetry: () => void;
  onSaved: (key?: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [action, setAction] = useState<ImportQualityReviewAction | null>(null);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [correctionApplied, setCorrectionApplied] = useState(false);
  const decisionPendingRef = useRef(false);
  const [correctionBusy, setCorrectionBusy] = useState(false);
  const [correctionEditing, setCorrectionEditing] = useState(false);
  const close = () => { if (correctionBusy || decisionPendingRef.current || decisionMutation.isPending) return; if (correctionApplied) onSaved(findingKey); else onClose(); };
  const open = Boolean(findingKey);
  const assessmentToken = finding?.assessment_token ?? null;
  const effectiveStatus = finding?.effective_status ?? null;

  useEscapeKey(close, open);

  const decisionMutation = useMutation({
    mutationFn: (args: {
      finding: ImportQualityReviewFinding;
      action: ImportQualityReviewAction;
      comment: string;
    }) =>
      recordImportQualityReviewDecision({
        supplier: args.finding.supplier,
        findingKind: args.finding.finding_kind,
        findingKey: args.finding.finding_key,
        assessmentToken: args.finding.assessment_token,
        action: args.action,
        comment: args.comment,
        requestId: createRequestId(),
      }),
    onSuccess: (_data, variables) => onSaved(variables.finding.finding_key),
    onSettled: () => { decisionPendingRef.current = false; },
    onError: (mutationError) => {
      if (mutationError instanceof ApiError && mutationError.status === 409) {
        setAction(null);
        setComment("");
        setReason("");
        void queryClient.invalidateQueries({
          queryKey: ["imports", "quality-review", "finding"],
        });
      }
    },
  });

  useEffect(() => {
    setAction(null);
    setComment("");
    setReason("");
    setCorrectionApplied(false);
  }, [assessmentToken, effectiveStatus, findingKey]);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus(),
    );
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      const returnTarget = returnFocusRef.current;
      returnFocusRef.current = null;
      if (returnTarget?.isConnected) {
        window.requestAnimationFrame(() => returnTarget.focus());
      }
    };
  }, [open]);

  if (!open) return null;
  const isParseReview = finding?.finding_kind === "parse_validation_line";
  const originalDocId = isParseReview ? stringValue(finding.evidence_snapshot.invoice_doc_id) : null;
  const currentStatus = finding?.effective_status ?? "open";
  const findingActive = finding?.active !== false;
  const allowedActions =
    currentStatus === "open"
      ? isParseReview ? [
          { value: "resolve_plausible" as const, label: "Abweichung ist erklärt", description: "Du hast den Hinweis anhand des Originals geklärt. Die übernommenen Werte bleiben bestehen." },
          { value: "mark_correction_required" as const, label: "Zur Klärung markieren", description: "Angaben sind falsch oder noch unklar. Die Werte werden dadurch noch nicht berichtigt." },
        ] : ACTION_OPTIONS
      : [
          {
            value: "reopen" as const,
            label: "Wieder öffnen",
            description: "Die bisherige Entscheidung bleibt im Verlauf erhalten.",
          },
        ];
  const reasons = isParseReview ? importReviewReasons(action) : [];
  const selectedReason = reasons.find((option) => option.value === reason)?.label ?? "";
  const savedComment = reviewDecisionComment(selectedReason, comment);
  const commentRequired = action !== null && !selectedReason;
  const saveDisabled =
    !finding ||
    !action ||
    decisionMutation.isPending || correctionBusy ||
    savedComment.length === 0 || savedComment.length > 1000;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="Prüfpunkt schließen"
        onClick={close}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-quality-finding-title"
        onKeyDown={trapDialogFocus}
        className={`absolute inset-y-0 right-0 flex ${isParseReview ? "w-[min(76rem,100vw)]" : "w-[min(42rem,100vw)]"} flex-col border-l border-slate-200 bg-white shadow-2xl`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {finding ? reviewKindConfig(finding.finding_kind).shortLabel : "Qualitätsprüfung"}
            </div>
            <h2
              id="import-quality-finding-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              {isParseReview ? "Rechnungsangaben prüfen" : finding?.title ?? "Prüfpunkt wird geladen"}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={close}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-xl text-slate-500 hover:bg-slate-50 hover:text-slate-800"
            aria-label="Prüfpunkt schließen"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {isLoading ? (
            <p className="text-sm text-slate-500">Prüfpunkt wird geladen.</p>
          ) : error || !finding ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              <p>{reviewLoadError(error)}</p>
              <button
                type="button"
                className={`${SECONDARY_BUTTON_CLASS} mt-3`}
                onClick={onRetry}
              >
                Erneut laden
              </button>
            </div>
          ) : (
            <div className={originalDocId ? "grid min-w-0 gap-6 lg:grid-cols-2" : ""}>
              {originalDocId && finding ? <QualityReviewOriginal key={finding.finding_key}
                docId={originalDocId} linePos={stringValue(finding.evidence_snapshot.line_pos)}
                docNumber={stringValue(finding.evidence_snapshot.doc_number)} /> : null}
              <div className="min-w-0 space-y-6">
              <section>
                <div className="flex flex-wrap items-center gap-2">
                  <ReviewStatusBadge
                    status={finding.effective_status}
                    active={findingActive}
                  />
                  <span className="text-xs text-slate-500">{finding.supplier}</span>
                </div>
                {!findingActive ? (
                  <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-700">
                    Dieser Prüfpunkt ist im aktuellen Importstand nicht mehr aktiv.
                    Die frühere Entscheidung und ihre Nachweise bleiben zur
                    Dokumentation sichtbar.
                  </div>
                ) : null}
                <p className="mt-3 text-sm leading-6 text-slate-700">
                  {finding.summary}
                </p>
                {isParseReview ? <p className="mt-2 text-sm leading-6 text-slate-600">
                  Vergleiche die übernommenen Angaben mit dem Original. Ein Hinweis bedeutet noch nicht, dass die Rechnung falsch ist.
                </p> : <div className="mt-3"><EvidenceLinks finding={finding} returnTo={returnTo} /></div>}
              </section>

              {!correctionApplied ? <EvidenceDetails finding={finding} /> : <p className="text-sm text-slate-600">Die früher angezeigten Prüfangaben wurden aktualisiert. Schließe diese Ansicht, um die verbleibenden Hinweise neu zu laden.</p>}
              {originalDocId && canDecide && !isDemoMode() && stringValue(finding.evidence_snapshot.line_pos) ? <fieldset disabled={decisionMutation.isPending} className="min-w-0"><LineAmountCorrection
                key={`${originalDocId}:${stringValue(finding.evidence_snapshot.line_pos)}`}
                docId={originalDocId} linePos={stringValue(finding.evidence_snapshot.line_pos)!}
                onApplied={() => setCorrectionApplied(true)} onBusyChange={setCorrectionBusy} onEditingChange={setCorrectionEditing} /></fieldset> : null}
              {isParseReview ? <details className="border-t border-slate-200 pt-3">
                <summary className="cursor-pointer text-sm font-medium text-slate-600">Weitere Nachweise</summary>
                <div className="mt-3"><EvidenceLinks finding={finding} returnTo={returnTo} /></div>
              </details> : null}

              {finding.latest_decision ? (
                <section className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Letzte Entscheidung
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <span className="font-medium text-slate-800">
                      {actionLabel(finding.latest_decision.action)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {formatDateTimeShort(finding.latest_decision.created_at)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {decisionActorLabel(finding.latest_decision)}
                    </span>
                  </div>
                  {finding.latest_decision.comment ? (
                    <p className="mt-2 rounded-md bg-white px-3 py-2 text-sm leading-5 text-slate-600">
                      {finding.latest_decision.comment}
                    </p>
                  ) : null}
                </section>
              ) : null}

              {!correctionApplied && !correctionEditing ? <section className="border-t border-slate-200 pt-5">
                <h3 className="text-sm font-semibold text-slate-900">{isParseReview ? "Was hat der Vergleich ergeben?" : "Entscheidung"}</h3>
                {historyMode ? (
                  <p className="mt-2 text-sm text-slate-600">
                    Der Verlauf ist schreibgeschützt. Öffnen Sie einen aktuellen
                    Prüfpunkt über einen Entscheidungsstatus, um ihn weiter zu
                    bearbeiten.
                  </p>
                ) : !findingActive ? (
                  <p className="mt-2 text-sm text-slate-600">
                    Für nicht mehr aktive Prüfpunkte kann keine neue Entscheidung
                    gespeichert werden.
                  </p>
                ) : !canDecide ? (
                  <p className="mt-2 text-sm text-slate-600">
                    Für diese Entscheidung fehlt Ihnen der Rollenbereich
                    Administrator.
                  </p>
                ) : (
                  <>
                    <div className={isParseReview ? "mt-3 divide-y divide-slate-200" : "mt-3 grid gap-2"}>
                      {allowedActions.map((option) => {
                        const selected = action === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            aria-pressed={selected}
                            disabled={decisionMutation.isPending || correctionBusy}
                            onClick={() => {
                              if (decisionPendingRef.current) return;
                              decisionMutation.reset();
                              if (action !== option.value) { setReason(""); setComment(""); }
                              setAction(option.value);
                            }}
                            className={[
                              isParseReview ? "block w-full rounded-md px-3 py-3 text-left transition" : "rounded-md border px-3 py-2 text-left transition",
                              selected
                                ? "border-emerald-300 bg-emerald-50 ring-1 ring-emerald-100"
                                : "border-slate-200 bg-white hover:bg-slate-50",
                            ].join(" ")}
                          >
                            <span className="block text-sm font-medium text-slate-900">
                              {option.label}
                            </span>
                            <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                              {option.description}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {reasons.length > 0 ? (
                      <div className="mt-4 space-y-1">
                        <span className="text-sm font-medium text-slate-800">Grund</span>
                        <FilterDropdown label="Grund" ariaLabel="Grund auswählen" layout="field"
                          disabled={decisionMutation.isPending || correctionBusy} value={reason} defaultValue=""
                          options={[{ value: "", label: "Eigene Begründung eingeben" }, ...reasons]}
                          onChange={setReason} />
                        <p className="text-xs leading-5 text-slate-500">Wähle nur einen Grund, den du am Original geprüft hast. Er wird mit deiner Entscheidung gespeichert.</p>
                      </div>
                    ) : null}
                    <label className="mt-4 block text-sm font-medium text-slate-800">
                      {selectedReason ? "Ergänzung (optional)" : `Begründung${commentRequired ? " *" : ""}`}
                      <textarea
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        rows={4}
                        maxLength={selectedReason ? 1000 - selectedReason.length - 2 : 1000}
                        disabled={decisionMutation.isPending || correctionBusy}
                        placeholder="Warum wurde diese Entscheidung getroffen?"
                        className="mt-1 block w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </label>
                    {savedComment.length > 1000 ? <p role="alert" className="mt-2 text-sm text-rose-700">Grund und Ergänzung sind zusammen zu lang. Bitte kürze den Text um {savedComment.length - 1000} Zeichen.</p> : null}
                    {decisionMutation.isError ? (
                      <p className="mt-3 text-sm text-rose-700">
                        {decisionSaveError(decisionMutation.error)}
                      </p>
                    ) : null}
                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        className={SECONDARY_BUTTON_CLASS}
                        onClick={close}
                      >
                        {isParseReview ? "Später prüfen" : "Abbrechen"}
                      </button>
                      <button
                        type="button"
                        className={PRIMARY_BUTTON_CLASS}
                        disabled={saveDisabled}
                        onClick={() => {
                          if (!finding || !action || saveDisabled || decisionPendingRef.current) return;
                          decisionPendingRef.current = true;
                          decisionMutation.mutate({
                            finding,
                            action,
                            comment: savedComment,
                          });
                        }}
                      >
                        {decisionMutation.isPending ? "Speichert…" : "Entscheidung speichern"}
                      </button>
                    </div>
                  </>
                )}
              </section> : null}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function trapDialogFocus(event: ReactKeyboardEvent<HTMLElement>) {
  if (event.key !== "Tab") return;
  const focusable = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      DIALOG_FOCUSABLE_SELECTOR,
    ),
  ).filter((element) => element.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !event.currentTarget.contains(active))) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && (active === last || !event.currentTarget.contains(active))) {
    event.preventDefault();
    first.focus();
  }
}

function EvidenceLinks({
  finding,
  returnTo,
  compact = false,
}: {
  finding: ImportQualityReviewFinding;
  returnTo: string;
  compact?: boolean;
}) {
  const evidence = finding.evidence_snapshot;
  const articleKey =
    stringValue(evidence.article_group_key) ?? stringValue(evidence.set_group_key);
  const invoiceDocId = stringValue(evidence.invoice_doc_id);
  const docNumber = stringValue(evidence.doc_number);
  const linePos = stringValue(evidence.line_pos);
  const concreteSource = concreteSourceLink(finding);
  const isPdfLineFinding =
    finding.finding_kind === "parse_validation_line" && Boolean(invoiceDocId);
  if (!compact) {
    return (
      <section className="rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">Nachweise</h3>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          {isPdfLineFinding
            ? "Prüfen Sie zuerst die betroffene Position im PDF. Das vollständige Dokument und weitere Quelldetails bleiben separat erreichbar."
            : "Prüfen Sie die konkreten Quellen und Abgleich-Nachweise. Weitere Beleg- oder Artikeldetails bleiben separat erreichbar."}
        </p>
        {invoiceDocId ? (
          <div className="mt-3">
            <DocumentPdfActions
              docId={invoiceDocId}
              docNumber={docNumber}
              linePos={linePos}
              previewActionLabel={
                finding.finding_kind === "parse_validation_line"
                  ? "Position im PDF ansehen"
                  : undefined
              }
              openActionLabel="Ganzes PDF öffnen"
            />
          </div>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-200 pt-3">
          {invoiceDocId ? (
            <Link
              to={`/belege/${encodeURIComponent(invoiceDocId)}#belegpositionen`}
              state={{ from: returnTo }}
              className="text-xs font-medium text-slate-600 hover:text-emerald-700 hover:underline"
            >
              Belegdetails
            </Link>
          ) : null}
          {articleKey ? (
            <Link
              to={`/articles/${encodeURIComponent(articleKey)}/nachweise`}
              state={{ from: returnTo }}
              className="text-xs font-medium text-slate-600 hover:text-emerald-700 hover:underline"
            >
              Artikel-Nachweise
            </Link>
          ) : null}
          {concreteSource ? (
            <a
              href={concreteSource.href}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-slate-600 hover:text-emerald-700 hover:underline"
            >
              {concreteSource.label} ↗
            </a>
          ) : null}
        </div>
      </section>
    );
  }
  return (
    <div
      className="flex flex-wrap gap-1.5"
      aria-label="Verfügbare Nachweise"
    >
      {invoiceDocId ? <EvidenceAvailabilityBadge label="PDF" /> : null}
      {articleKey ? <EvidenceAvailabilityBadge label="Artikel" /> : null}
      {concreteSource ? (
        <EvidenceAvailabilityBadge label={sourceEvidenceLabel(concreteSource.label)} />
      ) : null}
    </div>
  );
}

function EvidenceAvailabilityBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
      {label}
    </span>
  );
}

function sourceEvidenceLabel(label: string): string {
  if (label.startsWith("UGL")) return "UGL";
  if (label.startsWith("DATANORM")) return "DATANORM";
  return "Quelle";
}

function concreteSourceLink(
  finding: ImportQualityReviewFinding,
): { href: string; label: string } | null {
  const evidence = finding.evidence_snapshot;
  const memberSources = arrayValue(evidence.members).flatMap((value) => {
    const member = recordValue(value);
    return [
      {
        record: recordValue(member.price_source),
        fallbackKind: "datanorm" as const,
      },
      {
        record: recordValue(member.membership_source),
        fallbackKind: "datanorm" as const,
      },
    ];
  });
  const uglExampleSources = arrayValue(evidence.ugl_only_examples).map(
    (value) => ({
      record: recordValue(value),
      fallbackKind: "ugl" as const,
      label: "UGL-Beispielquelle öffnen",
    }),
  );
  const candidates: Array<{
    record: Record<string, unknown>;
    fallbackKind: "datanorm" | "ugl" | null;
    label?: string;
  }> = [
    { record: recordValue(evidence.datanorm), fallbackKind: "datanorm" },
    {
      record: recordValue(evidence.set_price_source),
      fallbackKind: "datanorm",
    },
    ...memberSources,
    { record: recordValue(evidence.ugl), fallbackKind: "ugl" },
    ...uglExampleSources,
    { record: recordValue(evidence.source), fallbackKind: null },
    { record: evidence, fallbackKind: null },
  ];
  for (const candidate of candidates) {
    const sourceRef = safeSourceRef(
      stringValue(candidate.record.source_ref),
    );
    const directHref = safeSourceHref(
      stringValue(candidate.record.source_href),
    );
    const href =
      directHref ??
      (sourceRef
        ? `/coverage?file=${encodeURIComponent(sourceRef)}`
        : null);
    if (!href) continue;
    const rawKind =
      stringValue(candidate.record.source_kind) ??
      stringValue(candidate.record.format) ??
      candidate.fallbackKind;
    const kind = rawKind?.toLowerCase() ?? "";
    return {
      href,
      label:
        candidate.label ??
        (kind.includes("ugl")
          ? "UGL-Quelle öffnen"
          : kind.includes("datanorm")
            ? "DATANORM-Quelle öffnen"
            : "Quelldatei öffnen"),
    };
  }
  return null;
}

function safeSourceHref(value: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

function safeSourceRef(value: string | null): string | null {
  return value && /^src:[a-f0-9]{12}$/.test(value) ? value : null;
}

function EvidenceDetails({ finding }: { finding: ImportQualityReviewFinding }) {
  if (finding.finding_kind === "parse_validation_line") {
    return <ParseEvidence evidence={finding.evidence_snapshot} />;
  }
  if (finding.finding_kind === "reconciliation_supplier_case") {
    return <ReconciliationEvidence evidence={finding.evidence_snapshot} />;
  }
  return <SetPriceEvidence evidence={finding.evidence_snapshot} />;
}

function ParseEvidence({ evidence }: { evidence: Record<string, unknown> }) {
  const pdf = recordValue(evidence.pdf);
  const datanorm = recordValue(evidence.datanorm);
  const checks = recordValue(evidence.checks);
  const qty = numberValue(pdf.qty);
  const price = numberValue(pdf.net_unit_price);
  const amount = numberValue(pdf.line_net_amount);
  const calculated = qty !== null && price !== null ? qty * price : null;
  const difference = calculated !== null && amount !== null ? amount - calculated : null;
  const arithmetic = stringValue(checks.arithmetic);
  const priceMismatch = stringValue(checks.list_price)?.startsWith("factor_");
  const description = stringValue(evidence.description) ?? "Position ohne Bezeichnung";
  const longDescription = description.length > 100;
  return (
    <section className="min-w-0">
      <h3 className="text-base font-semibold text-slate-900">Übernommene Angaben</h3>
      {longDescription ? <details key={description} className="group mt-2">
        <summary className="cursor-pointer list-none rounded-sm [&::-webkit-details-marker]:hidden">
          <span className="block truncate text-sm font-medium text-slate-900 group-open:hidden">{description}</span>
          <span className="mt-1 block text-xs font-medium text-slate-600 underline decoration-slate-300 underline-offset-2">
            <span className="group-open:hidden">Vollständige Bezeichnung anzeigen</span>
            <span className="hidden group-open:inline">Bezeichnung einklappen</span>
          </span>
        </summary>
        <p className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-slate-700">{description}</p>
      </details> : <p className="mt-2 break-words text-sm font-medium text-slate-900">{description}</p>}
      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
        <div className="min-w-0"><dt className="text-slate-500">Artikelnummer</dt><dd className="mt-0.5 break-all text-slate-800">{stringValue(evidence.article_code) ?? "–"}</dd></div>
        <div><dt className="text-slate-500">Position</dt><dd className="mt-0.5 text-slate-800">{stringValue(evidence.line_pos) ?? "–"}</dd></div>
        <div><dt className="text-slate-500">Belegdatum</dt><dd className="mt-0.5 text-slate-800">{formatDateShort(stringValue(evidence.doc_date))}</dd></div>
      </dl>
      <dl className="mt-4 divide-y divide-slate-200 text-sm [&>div]:flex [&>div]:flex-wrap [&>div]:justify-between [&>div]:gap-x-4 [&>div]:gap-y-1 [&>div]:py-2 [&_dd]:tabular-nums">
        <EvidenceValue label="Menge / Einheit" value={quantityWithUnit(pdf.qty, pdf.uom)} />
        <EvidenceValue label="Einzelpreis netto" value={formatEuro(price)} />
        <EvidenceValue label="Positionssumme netto" value={formatEuro(amount)} />
        {arithmetic === "mismatch" ? <>
          <EvidenceValue label="Menge × Einzelpreis" value={formatEuro(calculated)} />
          <EvidenceValue label="Unterschied zur Positionssumme" value={formatEuro(difference)} />
        </> : null}
        {priceMismatch ? <>
          <EvidenceValue label="Listenpreis aus dem PDF" value={formatEuro(numberValue(pdf.list_unit_price))} />
          <EvidenceValue label="Listenpreis aus DATANORM" value={formatEuro(numberValue(datanorm.list_price))} />
        </> : null}
        {checks.uom === "mismatch" ? <EvidenceValue label="Einheit aus DATANORM" value={stringValue(datanorm.uom)} /> : null}
      </dl>
      {arithmetic === "mismatch" ? <p className="mt-3 text-xs leading-5 text-slate-600">Vergleich mit den übernommenen Netto-Werten. Prüfe im Original auch Rabatt, Rundung und die Preiseinheit, zum Beispiel einen Preis je 100 Stück.</p> : null}
      {arithmetic === "ambiguous_price_semantics" ? <p className="mt-3 text-sm text-slate-600">Für eine verlässliche Berechnung fehlen eindeutige Preisangaben. Ein fehlender Wert bedeutet nicht automatisch 0 €.</p> : null}
    </section>
  );
}

function ReconciliationEvidence({ evidence }: { evidence: Record<string, unknown> }) {
  const causes = arrayValue(evidence.causes).map(recordValue);
  const fieldSummaries = arrayValue(evidence.field_summaries).map(recordValue);
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900">Belegabgleich</h3>
      <p className="mt-1 text-sm leading-6 text-slate-600">
        Belegnummern und zugehörige Positionswerte werden zwischen UGL und PDF
        verglichen. Die Originalquellen bleiben dabei unverändert.
      </p>
      {causes.length > 0 ? (
        <ul className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {causes.map((cause, index) => (
            <li key={`${stringValue(cause.code) ?? "cause"}-${index}`}>
              {reconciliationCauseLabel(stringValue(cause.code))}
            </li>
          ))}
        </ul>
      ) : null}
      <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <MetricValue label="UGL-Rechnungen" value={formatQty(numberValue(evidence.ugl_rechnung_doc_count))} />
        <MetricValue label="PDF-Rechnungen" value={formatQty(numberValue(evidence.pdf_rechnung_doc_count))} />
        <MetricValue label="Gemeinsame Belege" value={formatQty(numberValue(evidence.matched_doc_count))} />
        <MetricValue label="Nur in UGL" value={formatQty(numberValue(evidence.ugl_only_doc_count))} />
        <MetricValue label="Nur als PDF" value={formatQty(numberValue(evidence.pdf_only_doc_count))} />
        <MetricValue
          label="Nicht zugeordnete Positionen"
          value={formatPercent(numberValue(evidence.unmatched_line_rate), "ratio")}
        />
      </dl>
      {fieldSummaries.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-[520px] text-sm sm:min-w-full">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Vergleich</th>
                <th className="px-3 py-2 text-right">Paare</th>
                <th className="px-3 py-2 text-right">Abweichend</th>
                <th className="px-3 py-2 text-right">Quote</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fieldSummaries.map((summary, index) => (
                <tr key={`${stringValue(summary.field_name) ?? "field"}-${index}`}>
                  <td className="px-3 py-2 text-slate-700">
                    {reconciliationFieldLabel(stringValue(summary.field_name))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatQty(numberValue(summary.total_pairs))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatQty(numberValue(summary.mismatch_count))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatPercent(numberValue(summary.mismatch_rate), "ratio")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <DocumentExamples
        title="Beispiele ohne Gegenstück"
        uglExamples={arrayValue(evidence.ugl_only_examples)}
        pdfExamples={arrayValue(evidence.pdf_only_examples)}
      />
    </section>
  );
}

function reconciliationCauseLabel(code: string | null): string {
  if (code === "reconciliation_no_document_pairs") {
    return "Für UGL- und PDF-Rechnungen wurden keine gemeinsamen Belegnummern gefunden.";
  }
  if (code === "reconciliation_unmatched_lines") {
    return "Ein Teil der Belegpositionen konnte nicht eindeutig zugeordnet werden.";
  }
  if (code?.startsWith("reconciliation_") && code.endsWith("_mismatch_rate")) {
    const field = code.slice("reconciliation_".length, -"_mismatch_rate".length);
    return `${reconciliationFieldLabel(field)} weicht bei mehreren zugeordneten Positionen ab.`;
  }
  return "Der Belegabgleich enthält einen fachlich zu prüfenden Hinweis.";
}

function reconciliationFieldLabel(field: string | null): string {
  const labels: Record<string, string> = {
    article_code: "Artikelnummer",
    quantity: "Menge",
    unit: "Einheit",
    uom: "Einheit",
    net_unit_price: "Netto-Einheitspreis",
    list_unit_price: "Listenpreis",
    line_net_amount: "Zeilensumme",
  };
  return field ? (labels[field] ?? "Positionswert") : "Positionswert";
}

function SetPriceEvidence({ evidence }: { evidence: Record<string, unknown> }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900">Preisvergleich des Sets</h3>
      <dl className="mt-3 grid gap-x-6 gap-y-3 rounded-lg border border-slate-200 bg-white p-4 text-sm sm:grid-cols-2">
        <EvidenceValue label="Set" value={stringValue(evidence.set_code)} />
        <EvidenceValue label="Bestandteile" value={formatQty(numberValue(evidence.member_count))} />
        <EvidenceValue label="Davon mit Preis" value={formatQty(numberValue(evidence.priced_member_count))} />
        <EvidenceValue label="Fehlende Preise" value={formatQty(numberValue(evidence.missing_member_price_count))} />
        <EvidenceValue label="Setpreis" value={formatEuro(numberValue(evidence.set_list_price))} />
        <EvidenceValue label="Summe Einzelpreise" value={formatEuro(numberValue(evidence.member_sum_list_price))} />
        <EvidenceValue label="Differenz" value={formatEuro(numberValue(evidence.signed_delta))} />
        <EvidenceValue label="Abweichung" value={formatPercent(numberValue(evidence.relative_delta), "ratio")} />
        <EvidenceValue label="Prüfgrund" value={setPriceReasonLabel(stringValue(evidence.review_bucket))} />
      </dl>
    </section>
  );
}

function EvidenceValue({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{value || "–"}</dd>
    </div>
  );
}

function MetricValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}

function DocumentExamples({
  title,
  uglExamples,
  pdfExamples,
}: {
  title: string;
  uglExamples: unknown[];
  pdfExamples: unknown[];
}) {
  const examples = [
    ...uglExamples.map((item) => ({ source: "UGL", value: recordValue(item) })),
    ...pdfExamples.map((item) => ({ source: "PDF", value: recordValue(item) })),
  ].slice(0, 10);
  if (examples.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-slate-200">
      <h4 className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h4>
      <ul className="divide-y divide-slate-100">
        {examples.map((example, index) => (
          <li key={`${example.source}-${index}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
            <span className="font-medium text-slate-800">
              {stringValue(example.value.document_number) ?? "Beleg ohne Nummer"}
            </span>
            <span className="text-xs text-slate-500">
              {example.source} · {formatDateShort(stringValue(example.value.document_date))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewStatusBadge({
  status,
  active = true,
}: {
  status: string;
  active?: boolean;
}) {
  if (!active) {
    return (
      <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
        Nicht mehr aktiv
      </span>
    );
  }
  const config = reviewStatusConfig(status);
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function reviewStatusConfig(status: string) {
  if (status === "resolved") {
    return { label: "Plausibel", className: "bg-emerald-50 text-emerald-800" };
  }
  if (status === "action_required") {
    return { label: "Korrektur nötig", className: "bg-rose-50 text-rose-800" };
  }
  return { label: "Offen", className: "bg-amber-50 text-amber-800" };
}

function supplierFromParam(value: string | null): string {
  const normalized = value?.trim().toUpperCase() ?? "";
  return /^[A-Z0-9_-]{1,32}$/.test(normalized) ? normalized : "SHT";
}

function kindFromParam(value: string | null): ImportQualityReviewKind {
  return REVIEW_KINDS.some((item) => item.value === value)
    ? (value as ImportQualityReviewKind)
    : "parse_validation_line";
}

function statusFromParam(value: string | null): ReviewListStatus {
  return STATUS_OPTIONS.some((item) => item.value === value)
    ? (value as ReviewListStatus)
    : "open";
}

function normalizeFindingKey(value: string | null): string | null {
  const normalized = value?.trim() ?? "";
  if (!normalized || normalized.length > 240 || /[\u0000-\u001f\u007f]/.test(normalized)) {
    return null;
  }
  return normalized;
}

function reviewKindConfig(kind: ImportQualityReviewKind) {
  return REVIEW_KINDS.find((item) => item.value === kind) ?? REVIEW_KINDS[0];
}

function statusLabel(status: ReviewListStatus): string {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label ?? "Offen";
}

function historyResponseToReviewPage(
  response: ImportQualityReviewHistoryResponse,
): ImportQualityReviewResponse {
  return {
    supplier: response.supplier,
    kind: response.kind,
    status: "all",
    total: response.total,
    limit: response.limit,
    offset: response.offset,
    items: response.events.map((event) => ({
      finding_key: event.finding_key,
      finding_kind: event.finding_kind,
      supplier: event.supplier,
      title: event.title,
      summary: event.summary,
      severity: "review",
      effective_status: event.status,
      active: event.active,
      assessment_token: event.assessment_token,
      evidence_snapshot: event.evidence_snapshot,
      latest_decision: event,
      history_event_id: event.event_id,
    })),
  };
}

function actionLabel(action: ImportQualityReviewAction): string {
  if (action === "resolve_plausible") return "Plausibel";
  if (action === "mark_correction_required") return "Korrektur erforderlich";
  return "Wieder geöffnet";
}

function decisionActorLabel(
  decision: NonNullable<ImportQualityReviewFinding["latest_decision"]>,
): string {
  const label = decision.actor_label?.trim() ?? "";
  const technicalId = decision.actor_user_id?.trim() ?? "";
  if (label && label !== technicalId) return label;
  return technicalId ? "Benutzer" : "Administration";
}

function setPriceReasonLabel(value: string | null): string {
  const labels: Record<string, string> = {
    set_above_member_sum: "Setpreis über Summe der Einzelpreise",
    implausible_discount: "Rabatt nicht plausibel",
    set_price_mismatch: "Setpreis weicht ab",
    missing_set_price: "Setpreis fehlt",
    missing_members: "Bestandteile fehlen",
    missing_member_prices: "Preise von Bestandteilen fehlen",
  };
  return value ? (labels[value] ?? "Preisprüfung erforderlich") : "–";
}

function quantityWithUnit(quantity: unknown, unit: unknown): string {
  const quantityLabel = formatQty(numberValue(quantity));
  const unitLabel = stringValue(unit);
  return unitLabel ? `${quantityLabel} ${unitLabel}` : quantityLabel;
}

function stringValue(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function reviewLoadError(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) {
    return "Eine andere Qualitätsprüfung wird gerade geladen. Bitte versuchen Sie es gleich erneut.";
  }
  return "Die Qualitätsprüfung konnte nicht geladen werden.";
}

function decisionSaveError(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return "Der Prüfpunkt hat sich inzwischen geändert. Bitte schließen Sie ihn und öffnen Sie ihn erneut.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return "Für diese Entscheidung fehlt Ihnen der Rollenbereich Administrator.";
  }
  return "Die Entscheidung konnte nicht gespeichert werden.";
}

function createRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `quality-review-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
